// src/components/Layout.jsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShieldAlert, Users2, Home, LogOut, User, ClipboardList, FileText } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import InsyncGuardInfo from './InsyncGuardInfo';

export default function Layout({ children }) {
    const location = useLocation();
    const [status, setStatus] = React.useState(null);
    const [userMenuOpen, setUserMenuOpen] = React.useState(false);
    const userMenuRef = React.useRef(null);
    const { instance, accounts } = useMsal();
    const account = accounts[0];

    const handleLogout = () => {
        instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    };

    // Close user menu when clicking outside
    React.useEffect(() => {
        if (!userMenuOpen) return;
        const handleClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [userMenuOpen]);

    React.useEffect(() => {
        fetch('https://hgm6cn1mwxlj.statuspage.io/api/v2/status.json')
            .then(response => response.json())
            .then(data => setStatus(data.status))
            .catch(err => console.error('Failed to fetch status:', err));
    }, []);

    const getStatusColor = (indicator) => {
        switch (indicator) {
            case 'none': return 'bg-green-500';
            case 'minor': return 'bg-yellow-500';
            case 'major': return 'bg-orange-500';
            case 'critical': return 'bg-red-500';
            case 'maintenance': return 'bg-blue-500';
            default: return 'bg-gray-400';
        }
    };

    const navSections = [
        {
            heading: 'Main Menu',
            items: [
                { name: 'Home', path: '/', icon: Home, exact: true },
            ],
        },
        {
            heading: 'Email Service (IES)',
            items: [
                { name: 'IES Overview', path: '/email', icon: LayoutDashboard, prefix: '/email' },
                { name: 'Audit Trail', path: '/audit', icon: ClipboardList, exact: true },
            ],
        },
        {
            heading: 'Reporting',
            items: [
                { name: 'Subscriptions', path: '/report-subscriptions', icon: FileText, exact: true },
            ],
        },
        {
            heading: 'Coming Soon',
            items: [
                { name: 'Security', path: '/security', icon: ShieldAlert, disabled: true }, // Placeholder for future
                { name: 'Partner Connectivity', path: '/partner-connectivity', icon: Users2, disabled: true }, // Placeholder for future
            ],
        },
    ];

    const allNavItems = navSections.flatMap((section) => section.items);

    const isItemActive = (item) => {
        if (item.exact) {
            return location.pathname === item.path;
        }
        if (item.prefix) {
            return location.pathname.startsWith(item.prefix);
        }
        return location.pathname === item.path;
    };

    const currentView = allNavItems.find((item) => isItemActive(item));

    return (
        <div className="h-screen bg-gray-50 flex font-sans overflow-hidden">
            {/* Sidebar */}
            <div className="w-72 bg-slate-900 text-white flex-shrink-0 hidden md:flex flex-col h-screen sticky top-0">
                {/* Logo Area */}
                <div className="p-6 border-b border-slate-800 flex items-center gap-3">
                    <img src="/insync-digital-colour.png" alt="Insync Logo" className="h-10 w-10 rounded-md bg-white p-1" />
                    <div>
                        <h1 className="text-lg font-bold tracking-wide text-white">INSYNC INSURANCE</h1>
                        <p className="text-[10px] text-orange-500 font-semibold tracking-widest uppercase">Ops Console</p>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 mt-6 px-4 space-y-4">
                    {navSections.map((section) => (
                        <div key={section.heading} className="space-y-1">
                            <p className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{section.heading}</p>
                            {section.items.map((item) => {
                                const active = isItemActive(item);

                                return (
                                    <Link
                                        key={`${section.heading}-${item.name}`}
                                        to={item.disabled ? '#' : item.path}
                                        className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 group ${active
                                            ? 'bg-orange-600 text-white shadow-lg shadow-orange-900/20'
                                            : item.disabled
                                                ? 'text-slate-600 cursor-not-allowed'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                            }`}
                                    >
                                        <item.icon className={`mr-3 h-5 w-5 ${active ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                                        {item.name}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* User Section */}
                <div className="mt-auto border-t border-slate-800 p-4 flex-shrink-0">
                    <div className="relative" ref={userMenuRef}>
                        <button
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-800 transition-colors text-left"
                        >
                            <div className="h-9 w-9 rounded-full bg-orange-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                {account?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || <User className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{account?.name || 'User'}</p>
                                <p className="text-xs text-slate-400 truncate">{account?.username || ''}</p>
                            </div>
                        </button>

                        {userMenuOpen && (
                            <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-hidden z-50">
                                <div className="px-4 py-3 border-b border-slate-700">
                                    <p className="text-xs text-slate-400">Signed in as</p>
                                    <p className="text-sm font-medium text-white truncate">{account?.username}</p>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-400 hover:bg-slate-700 transition-colors"
                                >
                                    <LogOut className="h-4 w-4" />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                <header className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 flex justify-between items-center z-10 flex-shrink-0">
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Current View</span>
                        <span className="text-xl font-bold text-gray-900">
                            {currentView?.name || 'Dashboard'}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <a
                            href="https://insyncinsurance.statuspage.io"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors group"
                        >
                            <div className="flex items-center gap-2">
                                <span className={`h-2.5 w-2.5 rounded-full ${getStatusColor(status?.indicator)} ring-2 ring-white`}></span>
                                <span className="text-xs font-semibold text-gray-700 group-hover:text-orange-600 transition-colors">
                                    {status ? status.description : 'Checking Status...'}
                                </span>
                            </div>
                        </a>
                        <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{account?.name || 'User'}</span>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto bg-gray-50 p-8" style={{ scrollbarGutter: 'stable' }}>
                    <div className="max-w-7xl mx-auto">
                        {children}
                    </div>
                </main>

                {/* Footer */}
                <footer className="bg-white border-t border-gray-200 py-4 px-8 flex-shrink-0">
                    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-xs text-gray-500">
                        <p>&copy; {new Date().getFullYear()} Insync Insurance Solutions Ltd. All rights reserved.</p>
                        <div className="flex gap-6 mt-2 md:mt-0">
                            <a href="https://insyncinsurance.co.uk/bitnami/wordpress/wp-content/uploads/docs/Privacy%20Notice.pdf" target='_blank' rel="noreferrer" className="hover:text-orange-600 transition-colors">Privacy Policy</a>
                            <InsyncGuardInfo />
                            <span>v1.1.2-enterprise</span>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
}